'use client';

import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { Input } from '@/components/ui/forms/input';
import { Label } from '@/components/ui/forms/label';
import { Textarea } from '@/components/ui/forms/textarea';
import ImproveTextControl from '../ImproveTextControl';
import { WithCommentIndicator } from '@/components/features/resume/AnalysisCommentIndicator';
import type { ResumeAnalysisSuggestion } from '@/lib/resume/analysis';

interface ProfileSectionProps {
  guestMode?: boolean;
  onGuestAction?: () => void;
  /** Analysis suggestions targeting the professional summary. */
  commentSuggestions?: ResumeAnalysisSuggestion[];
  onApplyCommentSuggestion?: (suggestion: ResumeAnalysisSuggestion) => void;
  applyingSuggestionId?: string | null;
}

export default function ProfileSection({
  guestMode,
  onGuestAction,
  commentSuggestions,
  onApplyCommentSuggestion,
  applyingSuggestionId,
}: ProfileSectionProps) {
  const { resume, updateContent } = useResumeBuilderStore();

  if (!resume) return null;

  const { profile } = resume.content;

  const handleChange = (field: 'headline' | 'summary', value: string) => {
    updateContent({
      profile: {
        ...profile,
        [field]: value,
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="headline">Professional Headline</Label>
        <Input
          id="headline"
          value={profile.headline || ''}
          onChange={(e) => handleChange('headline', e.target.value)}
          placeholder="Senior Software Engineer"
          maxLength={200}
        />
        <p className="text-caption text-muted-foreground">
          A brief title that describes your professional identity.
        </p>
      </div>

      <WithCommentIndicator
        suggestions={commentSuggestions ?? []}
        onApply={onApplyCommentSuggestion ?? (() => {})}
        isApplying={applyingSuggestionId}
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="summary">Professional Summary</Label>
            <ImproveTextControl
              text={profile.summary || ''}
              field="summary"
              instructionPlaceholder="e.g., Make it more concise, highlight leadership skills..."
              onImproved={(improvedText) => handleChange('summary', improvedText)}
              guestMode={guestMode}
              onGuestAction={onGuestAction}
            />
          </div>
          <Textarea
            id="summary"
            value={profile.summary || ''}
            onChange={(e) => handleChange('summary', e.target.value)}
            placeholder="A results-driven software engineer with 8+ years of experience..."
            rows={6}
            maxLength={5000}
          />
          <p className="text-caption text-muted-foreground">
            {(profile.summary || '').length}/5000 characters
          </p>
        </div>
      </WithCommentIndicator>
    </div>
  );
}
